import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import React, { useState } from 'react';

import { cn } from '@/utils';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

export const Tooltip: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

export const TooltipTrigger = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('inline-flex', className)} {...props} />
));
TooltipTrigger.displayName = 'TooltipTrigger';

export const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { side?: 'top' | 'right' | 'bottom' | 'left' }
>(({ className, side = 'top', ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'absolute z-50 overflow-hidden rounded-md bg-foreground px-3 py-1.5 text-xs text-primary-foreground shadow-md animate-in fade-in-50',
        {
          'bottom-full left-1/2 -translate-x-1/2 mb-2': side === 'top',
          'left-full top-1/2 -translate-y-1/2 ml-2': side === 'right',
          'top-full left-1/2 -translate-x-1/2 mt-2': side === 'bottom',
          'right-full top-1/2 -translate-y-1/2 mr-2': side === 'left',
        },
        className,
      )}
      {...props}
    />
  );
});
TooltipContent.displayName = 'TooltipContent';

// AGENTS: Prefer SimpleTooltipV2 over SimpleTooltip for all new usage.
// SimpleTooltip uses position:absolute and will be clipped by overflow:hidden ancestors
// (e.g. Radix AccordionContent, ScrollArea). SimpleTooltipV2 renders via a portal and
// escapes all overflow constraints.

// Simple tooltip wrapper for basic use cases
// @deprecated Use SimpleTooltipV2 for new code.
export const SimpleTooltip: React.FC<TooltipProps> = ({
  children,
  content,
  side = 'top',
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className={cn('relative inline-flex', className)}>
      <div onMouseEnter={() => setIsVisible(!!content)} onMouseLeave={() => setIsVisible(false)}>
        {children}
      </div>
      {isVisible && (
        <div
          className={cn(
            'absolute z-50 overflow-hidden rounded-md bg-foreground px-3 py-1.5 text-xs text-primary-foreground shadow-md',
            'whitespace-nowrap',
            {
              'bottom-full left-1/2 -translate-x-1/2 mb-2': side === 'top',
              'left-full top-1/2 -translate-y-1/2 ml-2': side === 'right',
              'top-full left-1/2 -translate-x-1/2 mt-2': side === 'bottom',
              'right-full top-1/2 -translate-y-1/2 mr-2': side === 'left',
            },
          )}
        >
          {content}
        </div>
      )}
    </div>
  );
};

// Portal-based tooltip — use this for all new code.
// Renders outside the DOM tree via a portal so it is never clipped by
// overflow:hidden ancestors (Radix accordions, scroll areas, etc.).
// API is identical to SimpleTooltip.
export const SimpleTooltipV2: React.FC<TooltipProps> = ({
  children,
  content,
  side = 'top',
  className,
}) => {
  if (!content) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <div className={cn('inline-flex', className)}>{children}</div>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              'z-50 rounded-md bg-foreground px-3 py-1.5 text-xs text-primary-foreground shadow-md',
              'animate-in fade-in-0 zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            )}
          >
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};
